import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Avatar, Box, Button, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { DiagnosticColumns } from '@src/components/sco/Funding/FundingLandingDiagnostic/DiagnosticShell';
import { AccentHeading, CheckRow, formatCompactRaise, Panel, ProofStats, WhyWeAsk } from '@src/components/sco/Funding/FundingLandingDiagnostic/shared';
import { useRequestFunnelContext } from '@src/components/sco/RequestFunnel/RequestFunnelContext';
import { ScoInvestorProfile } from '@src/graphql-types';
import { gql } from '@src/lib/gql';
import tracker from '@src/lib/tracker';
import React, { useEffect } from 'react';
import { LuChevronRight } from 'react-icons/lu';
const INVESTOR_SEARCH_DRAFT_QUERY = gql`
  query FundingLandingDiagnosticMatches($selectedInvestorTagTitles: [String]) {
    scoInvestorSearchDraft(
      selectedInvestorTagTitles: $selectedInvestorTagTitles
      matchedInvestorProfilesLimit: 12
    ) {
      id
      matchedInvestorProfilesCount
      matchedInvestorProfiles {
        id
        title
        shortTitle
        logoUrl
        primaryInvestorType
        hqLocation
        totalInvestmentsTTMCount
        minimumInvestmentInDollars
        maximumInvestmentInDollars
        investorTags {
          id
          title
        }
        primaryInvestorProfileContact {
          id
          fullName
          title
        }
      }
    }
  }
`;
function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase() || '').join('');
}
function whyBlurb(investor: ScoInvestorProfile, industry?: string): string {
  const parts: string[] = [];
  const type = investor.primaryInvestorType;
  if (type) {
    parts.push(`Active ${type.toLowerCase()} that fits your stage`);
  }
  if (industry) {
    parts.push(`aligned with ${industry}`);
  }
  if (investor.totalInvestmentsTTMCount) {
    parts.push(`with ${investor.totalInvestmentsTTMCount} investments in the last 12 months`);
  }
  if (!parts.length) {
    return 'A strong fit for your industry and round profile.';
  }
  return `${parts.join(', ')}.`.replace(/^./, c => c.toUpperCase());
}
export const ResultsStep: React.FC = () => {
  const $ = _c(58);
  const {
    signupRequest,
    setStep
  } = useRequestFunnelContext();
  let t0;
  if ($[0] !== signupRequest.investorIndustryTagTitles) {
    t0 = signupRequest.investorIndustryTagTitles || [];
    $[0] = signupRequest.investorIndustryTagTitles;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const tags = t0;
  const industry = tags[0] || "your industry";
  let t1;
  if ($[2] !== signupRequest.fundingGoalInDollars) {
    t1 = formatCompactRaise(signupRequest.fundingGoalInDollars);
    $[2] = signupRequest.fundingGoalInDollars;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const raiseLabel = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(_temp, t2);
  let t3;
  if ($[5] !== tags) {
    t3 = {
      selectedInvestorTagTitles: tags
    };
    $[5] = tags;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = tags.length === 0;
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = {
      ssr: false,
      variables: t3,
      skip: t4
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const draftQuery = useQuery(INVESTOR_SEARCH_DRAFT_QUERY, t5);
  const draft = draftQuery.data?.scoInvestorSearchDraft;
  const matchCount = draft?.matchedInvestorProfilesCount || 0;
  const topMatch = draft?.matchedInvestorProfiles?.[0];
  const contactName = topMatch?.primaryInvestorProfileContact?.fullName || topMatch?.title || "Your top match";
  const investorType = topMatch?.primaryInvestorType || null;
  const t6 = topMatch?.primaryInvestorProfileContact?.fullName ? topMatch.title : investorType;
  const t7 = topMatch?.primaryInvestorProfileContact?.title;
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = [t6, t7].filter(Boolean);
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const firmLine = t8.join(" \xB7 ");
  let matchStats;
  if ($[13] !== investorType || $[14] !== tags[0] || $[15] !== topMatch) {
    matchStats = [];
    if (topMatch?.totalInvestmentsTTMCount) {
      const t9 = String(topMatch.totalInvestmentsTTMCount);
      let t10;
      if ($[17] !== t9) {
        t10 = {
          n: t9,
          l: "Deals \xB7 12 mo"
        };
        $[17] = t9;
        $[18] = t10;
      } else {
        t10 = $[18];
      }
      matchStats.push(t10);
    }
    if (investorType) {
      let t9;
      if ($[19] !== investorType) {
        t9 = {
          n: investorType,
          l: "Type"
        };
        $[19] = investorType;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      matchStats.push(t9);
    }
    if (tags[0]) {
      let t9;
      if ($[21] !== tags[0]) {
        t9 = {
          n: tags[0],
          l: "Industry"
        };
        $[21] = tags[0];
        $[22] = t9;
      } else {
        t9 = $[22];
      }
      matchStats.push(t9);
    }
    $[13] = investorType;
    $[14] = tags[0];
    $[15] = topMatch;
    $[16] = matchStats;
  } else {
    matchStats = $[16];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <AccentHeading accent="who can fund you." line1="Here are the investors" />;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== draftQuery.loading || $[25] !== matchCount) {
    t10 = draftQuery.loading ? "\u2026" : matchCount.toLocaleString();
    $[24] = draftQuery.loading;
    $[25] = matchCount;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = <Text as="span" fontWeight="bold">{t10}</Text>;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== industry || $[30] !== raiseLabel || $[31] !== t11) {
    t12 = <>{t11}{" "}investors who match {raiseLabel} {industry}</>;
    $[29] = industry;
    $[30] = raiseLabel;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <>Average of a{" "}<Text as="span" fontWeight="bold">94%</Text>{" "}match rating (very high!)</>;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t12) {
    t14 = <WhyWeAsk title="What we Found" items={[t12, t13, "Full step-by-step Funding Plan is ready"]} />;
    $[34] = t12;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ProofStats />;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t14) {
    t16 = <>{t9}{t14}{t15}</>;
    $[37] = t14;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== contactName || $[40] !== draftQuery.loading || $[41] !== firmLine || $[42] !== industry || $[43] !== matchStats || $[44] !== tags.length || $[45] !== topMatch) {
    t17 = <Panel>{draftQuery.loading || !topMatch && tags.length > 0 ? <VStack colorPalette="orange" gap={3} paddingY={8}><Spinner color="colorPalette.solid" size="lg" /><Text color="fg.muted" fontSize="sm">Pulling your investor matches…</Text></VStack> : topMatch ? <><Text color="fg.muted" fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase">Your #1 match</Text><Box bg="bg.subtle" borderColor="border.emphasized" borderRadius="2xl" borderWidth="1px" overflow="hidden" textAlign="start"><HStack align="center" gap={3} padding={3}><Avatar.Root borderRadius="full" size="md"><Avatar.Fallback name={contactName}>{initials(contactName)}</Avatar.Fallback><Avatar.Image src={topMatch.logoUrl} /></Avatar.Root><Box flex={1} minWidth={0}><Text truncate={true} color="fg" fontSize="md" fontWeight="extrabold">{contactName}</Text>{firmLine && <Text truncate={true} color="fg.muted" fontSize="xs">{firmLine}</Text>}</Box><Box bg="colorPalette.subtle" borderColor="colorPalette.border" borderRadius="full" borderWidth="1px" color="colorPalette.fg" colorPalette="green" flexShrink={0} fontSize="sm" fontWeight="extrabold" px={3} py={1}>{97}%</Box></HStack><Box paddingBottom={3} paddingX={3}><Box bg="colorPalette.subtle" borderColor="colorPalette.border" borderRadius="xl" borderWidth="1px" colorPalette="orange" padding={3}><Text color="colorPalette.fg" fontSize="2xs" fontWeight="bold" letterSpacing="wider" marginBottom={1} textTransform="uppercase">Why we like {contactName.split(" ")[0]}</Text><Text color="fg" fontSize="sm" lineHeight="short">{whyBlurb(topMatch, industry)}</Text></Box>{matchStats.length > 0 && <HStack gap={2} marginTop={3}>{matchStats.map(_temp2)}</HStack>}</Box></Box></> : <VStack gap={2} paddingY={6}><CheckRow>Add an industry to unlock ranked matches</CheckRow><Text color="fg.muted" fontSize="sm">Go back and pick an industry chip to continue.</Text></VStack>}</Panel>;
    $[39] = contactName;
    $[40] = draftQuery.loading;
    $[41] = firmLine;
    $[42] = industry;
    $[43] = matchStats;
    $[44] = tags.length;
    $[45] = topMatch;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== setStep) {
    t18 = () => setStep("register");
    $[47] = setStep;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <LuChevronRight />;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t18) {
    t20 = <Button fontWeight="bold" size="xl" variant="cta" width="full" onClick={t18}>See all Investor Matches{t19}</Button>;
    $[50] = t18;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t17 || $[53] !== t20) {
    t21 = <>{t17}{t20}</>;
    $[52] = t17;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t16 || $[56] !== t21) {
    t22 = <DiagnosticColumns left={t16} right={t21} />;
    $[55] = t16;
    $[56] = t21;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  return t22;
};
function _temp() {
  tracker.funnelStep(4);
}
function _temp2(stat) {
  return <VStack key={stat.l} bg="bg.muted" borderColor="border" borderRadius="xl" borderWidth="1px" flex={1} gap={0} paddingY={2}><Text truncate={true} color="fg" fontSize="sm" fontWeight="extrabold" maxW="full">{stat.n}</Text><Text color="fg.muted" fontSize="2xs" textTransform="uppercase">{stat.l}</Text></VStack>;
}